import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DOCUMENT } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, Renderer2 } from '@angular/core';
import { MatIconRegistry } from '@angular/material/icon';
import { RouterOutlet, Router, NavigationEnd } from '@angular/router';
import { ActivatedRoute } from '@angular/router';
import { Platform } from '@angular/cdk/platform';
import { filter } from 'rxjs/operators';
import { SidenavService } from '@layout/sidenav/sidenav.service';
import { ThemeService } from '@core/tema/theme.service';
import { SplashScreenService } from '@core/tema/splash-screen.service';
import { SidenavItem } from '@layout/sidenav/sidenav-item/sidenav-item.interface';
import {
  CIDADAO_SIDENAV_ITEMS,
  CIDADAO_SIDENAV_ITEM_ESIC,
} from '@features/ouvidoria/pages/cidadao-principal/cidadao-sidenav-items';
import { podeAbrirPedidoNoEsic } from '@core/contexto/acesso-modulos';
import { AuthService } from '@core/auth/auth.service';
import { EntidadeContextoService } from '@core/contexto/entidade-contexto.service';
import { ESIC_ADMIN_SIDENAV_ITEMS } from '@features/esic/pages/principal/esic-sidenav-items';
import { ESIC_CIDADAO_SIDENAV_ITEMS } from '@features/esic/pages/cidadao-principal/esic-cidadao-sidenav-items';

const ADMIN_SIDENAV_ITEMS: SidenavItem[] = [
  {
    name: 'PRINCIPAL',
    position: 5,
    type: 'subheading',
    customClass: 'first-subheading'
  },
  {
    name: 'Início',
    routeOrFunction: '/modulo/ouvidoria/admin/principal',
    icon: 'home',
    position: 10,
    pathMatchExact: true
  },
  {
    name: 'Consulta Manifestação',
    routeOrFunction: '/modulo/ouvidoria/admin/manifestacao/consulta',
    icon: 'search',
    position: 17,
  },
  {
    name: 'Painel de Controle',
    routeOrFunction: '/modulo/ouvidoria/admin/paineldecontrole',
    icon: 'dashboard',
    position: 18,
  },
  {
    name: 'CADASTROS',
    type: 'subheading',
    position: 35
  },
  {
    name: 'Cadastros auxiliares',
    icon: 'description',
    position: 45,
    subItems: [
      {
        name: 'Avaliação',
        routeOrFunction: '/modulo/ouvidoria/admin/opcoesparaavaliacao/list',
        position: 5
      },
      {
        name: 'Órgão',
        routeOrFunction: '/modulo/ouvidoria/admin/orgaos/list',
        position: 6
      },
      {
        name: 'Localidade',
        routeOrFunction: '/modulo/ouvidoria/admin/localidades/list',
        position: 7
      },
      {
        name: 'Resposta padrão',
        routeOrFunction: '/modulo/ouvidoria/admin/respostapadrao/list',
        position: 8
      },
    ]
  },
  {
    name: 'Pessoa',
    icon: 'people',
    position: 50,
    subItems: [
      {
        name: 'Consulta',
        routeOrFunction: '/modulo/ouvidoria/admin/pessoa/consulta',
        position: 1
      },
      {
        name: 'Pessoa jurídica',
        routeOrFunction: '/modulo/ouvidoria/admin/pessoajuridica',
        position: 2
      },
      {
        name: 'Pessoa física',
        routeOrFunction: '/modulo/ouvidoria/admin/pessoafisica',
        position: 3
      },
    ]
  },
  {
    name: 'ADMINISTRAÇÃO',
    type: 'subheading',
    position: 55
  },
  {
    name: 'Configuração',
    routeOrFunction: '/modulo/ouvidoria/admin/configuracao/geral',
    icon: 'settings',
    position: 56,
  },
  {
    name: 'Usuários',
    routeOrFunction: '/modulo/ouvidoria/admin/servidores/list',
    icon: 'supervisor_account',
    position: 57,
  },
];

@Component({
  selector: 'fury-root',
  imports: [RouterOutlet],
  templateUrl: './app.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppComponent {

  private readonly sidenavService = inject(SidenavService);
  private readonly iconRegistry = inject(MatIconRegistry);
  private readonly renderer = inject(Renderer2);
  private readonly themeService = inject(ThemeService);
  private readonly document = inject<Document>(DOCUMENT);
  private readonly platform = inject(Platform);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly splashScreenService = inject(SplashScreenService);
  private readonly auth = inject(AuthService);
  private readonly entidadeContexto = inject(EntidadeContextoService);

  private currentMenu: 'admin' | 'cidadao' | 'esic-admin' | 'esic-cidadao' | null = null;

  /** Ver {@link podeAbrirPedidoNoEsic} — as duas condições que liberam o atalho para o e-SIC. */
  private podeAbrirPedidoNoEsic(): boolean {
    return podeAbrirPedidoNoEsic(this.entidadeContexto.possuiEsic(), this.auth.roles());
  }

  constructor() {
    this.route.queryParamMap.pipe(
      filter(queryParamMap => queryParamMap.has('style')),
      takeUntilDestroyed()
    ).subscribe(queryParamMap => this.themeService.setStyle(queryParamMap.get('style') ?? 'default'));

    this.iconRegistry.setDefaultFontSetClass('material-icons');
    this.themeService.theme$.pipe(takeUntilDestroyed()).subscribe(theme => {
      if (theme[0]) {
        this.renderer.removeClass(this.document.body, theme[0]);
      }

      this.renderer.addClass(this.document.body, theme[1]);
    });

    if (this.platform.BLINK) {
      this.renderer.addClass(this.document.body, 'is-blink');
    }

    // Set initial menu based on current URL
    this.applySidenavForUrl(this.router.url);

    // Switch menu on navigation
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      takeUntilDestroyed()
    ).subscribe(event => {
      this.applySidenavForUrl(event.urlAfterRedirects);
    });
  }

  private applySidenavForUrl(url: string) {
    if (url.includes('/modulo/esic/cidadao')) {
      if (this.currentMenu !== 'esic-cidadao') {
        this.currentMenu = 'esic-cidadao';
        this.sidenavService.items = [];
        this.sidenavService.addItems(ESIC_CIDADAO_SIDENAV_ITEMS);
      }
    } else if (url.includes('/modulo/esic/')) {
      if (this.currentMenu !== 'esic-admin') {
        this.currentMenu = 'esic-admin';
        this.sidenavService.items = [];
        this.sidenavService.addItems(ESIC_ADMIN_SIDENAV_ITEMS);
      }
    } else if (url.includes('/modulo/ouvidoria/cidadao')) {
      // O atalho para o e-SIC entra condicionalmente, e a condição pode mudar dentro da mesma
      // sessão (o login grava o contexto da entidade), então o menu do cidadão é remontado a cada
      // navegação em vez de só na troca de módulo.
      const itens = this.podeAbrirPedidoNoEsic()
        ? [...CIDADAO_SIDENAV_ITEMS, CIDADAO_SIDENAV_ITEM_ESIC]
        : CIDADAO_SIDENAV_ITEMS;
      this.currentMenu = 'cidadao';
      this.sidenavService.items = [];
      this.sidenavService.addItems(itens);
    } else {
      if (this.currentMenu !== 'admin') {
        this.currentMenu = 'admin';
        this.sidenavService.items = [];
        this.sidenavService.addItems(ADMIN_SIDENAV_ITEMS);
      }
    }
  }
}
